import { useEffect, useMemo, useState } from "react";
import {
  GraduationCap,
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  CalendarPlus,
  PlayCircle,
  Sparkles,
  X,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { useTrainingData } from "../hooks/useTrainingData";
import type { TrainingFormat, TrainingSession } from "../types/navigator";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function parseDate(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDay(date: Date): string {
  return String(date.getDate());
}

function formatMonth(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
}

function formatWeekday(date: Date): string {
  return date.toLocaleDateString(undefined, { weekday: "short" });
}

function formatLongDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function durationMinutes(start: Date, end: Date): number {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

function formatDuration(start: Date, end: Date): string {
  const mins = durationMinutes(start, end);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function timeUntil(target: Date, now: Date): string {
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return "Happening now";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `Starts in ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Starts in ${hours} hr`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Starts tomorrow";
  if (days < 14) return `Starts in ${days} days`;
  const weeks = Math.floor(days / 7);
  return `Starts in ${weeks} weeks`;
}

function formatICSDate(date: Date): string {
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

function buildICS(s: TrainingSession): string {
  const start = parseDate(s.start);
  const end = parseDate(s.end);
  if (!start || !end) return "";
  const escape = (v: string) =>
    v.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Brookfield//AI Navigator//EN",
    "BEGIN:VEVENT",
    `UID:${s.id}@ai-navigator.brookfield`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    `DTSTART:${formatICSDate(start)}`,
    `DTEND:${formatICSDate(end)}`,
    `SUMMARY:${escape(s.title)}`,
    `DESCRIPTION:${escape(s.description)}`,
    s.location ? `LOCATION:${escape(s.location)}` : "",
    s.joinUrl ? `URL:${escape(s.joinUrl)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

function downloadICS(s: TrainingSession) {
  const ics = buildICS(s);
  if (!ics) return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${s.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function isJoinableNow(start: Date, end: Date, now: Date): boolean {
  const fifteen = 15 * 60 * 1000;
  return now.getTime() >= start.getTime() - fifteen && now.getTime() <= end.getTime();
}

type TabKey = "upcoming" | "past" | "all";

const TABS: { key: TabKey; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All" },
];

function CalendarDateBlock({
  date,
  size = "md",
}: {
  date: Date;
  size?: "sm" | "md" | "lg";
}) {
  const widths = { sm: 56, md: 68, lg: 88 };
  const dayFont = { sm: 22, md: 28, lg: 38 };
  const monthFont = { sm: 10, md: 11, lg: 12 };
  const w = widths[size];
  return (
    <div
      aria-hidden
      className="training-cal"
      style={{ width: w, fontFamily: "inherit" }}
    >
      <div
        className="training-cal-month"
        style={{ fontSize: monthFont[size] }}
      >
        {formatMonth(date)}
      </div>
      <div
        className="training-cal-day"
        style={{
          padding: size === "lg" ? "10px 0 8px" : "6px 0 4px",
          fontSize: dayFont[size],
        }}
      >
        {formatDay(date)}
      </div>
      <div className="training-cal-weekday">{formatWeekday(date)}</div>
    </div>
  );
}

function FormatBadge({ format }: { format: TrainingFormat }) {
  const map: Record<TrainingFormat, { label: string; cls: string }> = {
    virtual: { label: "Virtual", cls: "is-virtual" },
    "in-person": { label: "In person", cls: "is-inperson" },
    hybrid: { label: "Hybrid", cls: "is-hybrid" },
  };
  const v = map[format];
  return (
    <span className={`training-format-badge ${v.cls}`}>
      <Video size={11} aria-hidden />
      {v.label}
    </span>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  href,
  disabled,
  variant = "default",
  title,
}: {
  icon: typeof Calendar;
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  variant?: "default" | "primary";
  title?: string;
}) {
  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    border: "1px solid var(--border-strong)",
    background: variant === "primary" ? "var(--accent, #2563eb)" : "var(--surface-1)",
    color: variant === "primary" ? "white" : "var(--text)",
    opacity: disabled ? 0.5 : 1,
    transition: "transform 140ms ease, background 140ms ease",
    textDecoration: "none",
  };
  const content = (
    <>
      <Icon size={14} aria-hidden />
      {label}
    </>
  );
  if (href && !disabled) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
        title={title}
      >
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      style={style}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
    >
      {content}
    </button>
  );
}

function SessionActions({
  session,
  now,
  layout = "row",
}: {
  session: TrainingSession;
  now: Date;
  layout?: "row" | "stack";
}) {
  const start = parseDate(session.start);
  const end = parseDate(session.end);
  const isPast = !!end && end.getTime() < now.getTime();
  const canJoin =
    !isPast && start && end && session.joinUrl && isJoinableNow(start, end, now);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: layout === "row" ? "row" : "column",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      {isPast ? (
        <>
          <ActionButton
            icon={PlayCircle}
            label={session.recordingUrl ? "Watch recording" : "Recording pending"}
            href={session.recordingUrl}
            disabled={!session.recordingUrl}
            variant="primary"
            title={
              session.recordingUrl
                ? "Open the session recording"
                : "Recording not yet posted"
            }
          />
          {session.materialsUrl ? (
            <ActionButton
              icon={Sparkles}
              label="View materials"
              href={session.materialsUrl}
              title="Open slides and resources"
            />
          ) : null}
        </>
      ) : (
        <>
          <ActionButton
            icon={Video}
            label="Join meeting"
            href={canJoin ? session.joinUrl : undefined}
            disabled={!canJoin}
            variant="primary"
            title={
              canJoin
                ? "Join the Teams meeting"
                : "Join opens 15 minutes before the session starts"
            }
          />
          <ActionButton
            icon={CalendarPlus}
            label="Add to calendar"
            onClick={() => downloadICS(session)}
            title="Download an .ics file to add to your calendar"
          />
        </>
      )}
    </div>
  );
}

function HeroNextUp({
  session,
  onOpen,
  now,
}: {
  session: TrainingSession;
  onOpen: () => void;
  now: Date;
}) {
  const start = parseDate(session.start);
  const end = parseDate(session.end);
  if (!start || !end) return null;

  return (
    <div className="training-hero">
      <div aria-hidden className="training-hero-ribbon">
        <Sparkles size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
        Next up
      </div>

      <div
        style={{
          display: "flex",
          gap: 20,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <CalendarDateBlock date={start} size="lg" />

        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="training-hero-pill">
            <Clock size={11} aria-hidden />
            {timeUntil(start, now)}
          </div>
          <h2
            style={{
              margin: "0 0 4px",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--text)",
              lineHeight: 1.25,
            }}
          >
            {session.title}
          </h2>
          {session.sessionLabel ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {session.sessionLabel}
            </div>
          ) : null}
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: 14,
              lineHeight: 1.5,
              margin: "0 0 14px",
              maxWidth: 640,
            }}
          >
            {session.description}
          </p>

          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "center",
              fontSize: 13,
              color: "var(--text-muted)",
              marginBottom: 16,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Calendar size={14} aria-hidden />
              {formatLongDate(start)}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Clock size={14} aria-hidden />
              {formatTime(start)} – {formatTime(end)} · {session.timezone}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <MapPin size={14} aria-hidden />
              {session.location || "TBD"}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Users size={14} aria-hidden />
              Hosted by {session.host}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <SessionActions session={session} now={now} />
            <button
              type="button"
              onClick={onOpen}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "8px 12px",
                background: "transparent",
                border: "none",
                color: "var(--text-muted)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              View details
              <ChevronRight size={14} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionCard({
  session,
  onOpen,
  now,
}: {
  session: TrainingSession;
  onOpen: () => void;
  now: Date;
}) {
  const start = parseDate(session.start);
  const end = parseDate(session.end);
  const isPast = !!end && end.getTime() < now.getTime();

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="training-card"
      style={{ opacity: isPast ? 0.78 : 1 }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {start ? <CalendarDateBlock date={start} size="md" /> : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <FormatBadge format={session.format} />
            {isPast ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--surface-strong)",
                  color: "var(--text-muted)",
                }}
              >
                Past
              </span>
            ) : null}
            {session.tags?.map((t) => (
              <span
                key={t}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--surface-strong)",
                  color: "var(--text-muted)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <div
            style={{
              color: "var(--text)",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: 1.3,
            }}
          >
            {session.title}
          </div>
          {session.sessionLabel ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 12,
                fontWeight: 600,
                marginTop: 2,
              }}
            >
              {session.sessionLabel}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          fontSize: 12.5,
          color: "var(--text-muted)",
        }}
      >
        {start && end ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Clock size={12} aria-hidden />
            {formatTime(start)} – {formatTime(end)} · {formatDuration(start, end)}
          </div>
        ) : null}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <MapPin size={12} aria-hidden />
          {session.location || "TBD"}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Users size={12} aria-hidden />
          Hosted by {session.host}
        </div>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: "var(--text)",
          lineHeight: 1.5,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {session.description}
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          paddingTop: 8,
          borderTop: "1px solid var(--border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <SessionActions session={session} now={now} />
      </div>
    </article>
  );
}

function TrainingModal({
  session,
  onClose,
  now,
}: {
  session: TrainingSession;
  onClose: () => void;
  now: Date;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const start = parseDate(session.start);
  const end = parseDate(session.end);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-modal-title"
      onClick={onClose}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <div className="modal-head">
          <div aria-hidden className="training-modal-icon">
            <GraduationCap size={20} />
          </div>
          <div className="modal-head-text">
            <span className="modal-kind">AI Training</span>
            <h3 id="training-modal-title" className="modal-title">
              {session.title}
            </h3>
            {session.sessionLabel ? (
              <div
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                {session.sessionLabel}
              </div>
            ) : null}
            <div
              className="modal-badges"
              style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}
            >
              <FormatBadge format={session.format} />
              {session.tags?.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "var(--surface-strong)",
                    color: "var(--text-muted)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {start && end ? (
            <section className="modal-section">
              <h4>When & where</h4>
              <div className="meta-grid">
                <div className="meta-item">
                  <span className="meta-label">Date</span>
                  <span className="meta-value">{formatLongDate(start)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Time</span>
                  <span className="meta-value">
                    {formatTime(start)} – {formatTime(end)} · {session.timezone}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Duration</span>
                  <span className="meta-value">{formatDuration(start, end)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Location</span>
                  <span className="meta-value">{session.location || "TBD"}</span>
                </div>
              </div>
            </section>
          ) : null}

          {session.description ? (
            <section className="modal-section">
              <h4>About this session</h4>
              <p>{session.description}</p>
            </section>
          ) : null}

          <section className="modal-section">
            <h4>Host</h4>
            <div className="meta-grid">
              <div className="meta-item">
                <span className="meta-label">Hosted by</span>
                <span className="meta-value">{session.host}</span>
              </div>
            </div>
          </section>
        </div>

        <div className="modal-foot" style={{ flexWrap: "wrap", gap: 8 }}>
          <SessionActions session={session} now={now} layout="row" />
          <button className="btn btn-primary" onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function TrainingPage() {
  const [now, setNow] = useState<Date>(() => new Date());
  const [tab, setTab] = useState<TabKey>("upcoming");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { sessions, loading, error, reload } = useTrainingData();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const { upcoming, past } = useMemo(() => {
    const up: TrainingSession[] = [];
    const pa: TrainingSession[] = [];
    for (const s of sessions) {
      const end = parseDate(s.end);
      if (end && end.getTime() < now.getTime()) pa.push(s);
      else up.push(s);
    }
    up.sort(
      (a, b) => (parseDate(a.start)?.getTime() ?? 0) - (parseDate(b.start)?.getTime() ?? 0)
    );
    pa.sort(
      (a, b) => (parseDate(b.start)?.getTime() ?? 0) - (parseDate(a.start)?.getTime() ?? 0)
    );
    return { upcoming: up, past: pa };
  }, [now, sessions]);

  const visible = tab === "upcoming" ? upcoming : tab === "past" ? past : [...upcoming, ...past];
  const nextUp = upcoming[0];
  const selected = sessions.find((s) => s.id === selectedId) ?? null;

  const counts = {
    upcoming: upcoming.length,
    past: past.length,
    all: upcoming.length + past.length,
  };

  return (
    <div className="page">
      <div className="page-hero">
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <p className="page-subtitle">
              Live sessions, workshops, and office hours to help Brookfield teams
              get hands-on with AI. Sign up, join a session, or revisit recordings
              and materials from past trainings.
            </p>
          </div>
        </div>
      </div>

      {loading && sessions.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "60px 24px",
            textAlign: "center",
            background: "var(--surface-1)",
            border: "1px dashed var(--border)",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <GraduationCap
            size={36}
            style={{ color: "var(--text-muted)" }}
            aria-hidden
          />
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            Loading training sessions…
          </div>
        </div>
      ) : null}

      {error && !loading ? (
        <div role="alert" className="training-error">
          <AlertTriangle
            size={18}
            className="training-error-icon"
            aria-hidden
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
              Couldn't load training sessions
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "var(--text-muted)",
                marginTop: 2,
                wordBreak: "break-word",
              }}
            >
              {error}
            </div>
          </div>
          <button
            type="button"
            onClick={reload}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid var(--border-strong)",
              background: "var(--surface-1)",
              color: "var(--text)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {nextUp && tab !== "past" ? (
        <HeroNextUp
          session={nextUp}
          onOpen={() => setSelectedId(nextUp.id)}
          now={now}
        />
      ) : null}

      <div
        role="tablist"
        aria-label="Training filter"
        style={{
          display: "inline-flex",
          gap: 4,
          padding: 4,
          background: "var(--surface-strong)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          marginBottom: 20,
        }}
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 14px",
                borderRadius: 7,
                border: "none",
                background: active ? "var(--surface-1)" : "transparent",
                color: active ? "var(--text)" : "var(--text-muted)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: active ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                transition: "background 140ms ease, color 140ms ease",
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 11,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: active ? "var(--surface-strong)" : "transparent",
                  color: "var(--text-muted)",
                  fontWeight: 600,
                }}
              >
                {counts[t.key]}
              </span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 && !loading && !error ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "60px 24px",
            textAlign: "center",
            background: "var(--surface-1)",
            border: "1px dashed var(--border-strong)",
            borderRadius: 12,
          }}
        >
          <GraduationCap
            size={36}
            style={{ color: "var(--text-muted)" }}
            aria-hidden
          />
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {tab === "upcoming"
              ? "No upcoming sessions yet"
              : tab === "past"
                ? "No past sessions to show"
                : "Nothing scheduled"}
          </div>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: 13,
              maxWidth: 380,
              margin: 0,
            }}
          >
            New trainings will appear here as they're scheduled.
          </p>
        </div>
      ) : visible.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {visible.map((s) => (
            <SessionCard
              key={s.id}
              session={s}
              onOpen={() => setSelectedId(s.id)}
              now={now}
            />
          ))}
        </div>
      ) : null}

      {selected ? (
        <TrainingModal
          session={selected}
          onClose={() => setSelectedId(null)}
          now={now}
        />
      ) : null}
    </div>
  );
}
